import { useState, useEffect } from 'react'

export interface StateWithVersion<T> {
  __state: T
  __version: number
}

interface Props<T> {
  key: string
  initialState: (() => T) | T
  version?: number
  persistenceEnabled?: boolean
  storageType?: 'local' | 'session'
}

function usePersistedState<T>({ key, initialState, version = 1, persistenceEnabled = true, storageType = 'local' }: Props<T>) {
  const storage = storageType === 'session' ? window.sessionStorage : window.localStorage

  function wrappedInitState(): T {
    if (typeof initialState === 'function') {
      return (initialState as () => T)()
    } else {
      return initialState
    }
  }

  function initializer(): T {
    if (!persistenceEnabled) {
      return wrappedInitState()
    }

    try {
      const valueFromStorage = storage.getItem(key)

      if (!valueFromStorage) {
        return wrappedInitState()
      }

      const decodedState = JSON.parse(valueFromStorage) as StateWithVersion<T>

      if (decodedState.__state === undefined) return wrappedInitState()

      if (decodedState.__version !== version) {
        console.warn(`version mismatch for ${key}: ${version} <=> ${decodedState.__version}`)
        return wrappedInitState()
      }

      return decodedState.__state
    } catch (error) {
      console.warn(`decode error for ${key}: ${error}`)
      return wrappedInitState()
    }
  }

  const [state, setState] = useState<T>(initializer)

  useEffect(() => {
    if (!persistenceEnabled) return

    try {
      const stateWithVersion: StateWithVersion<T> = { __state: state, __version: version }
      storage.setItem(key, JSON.stringify(stateWithVersion))
    } catch (error) {
      console.error('could not store to local storage', error)
    }
  }, [key, state, version, persistenceEnabled, storage])

  return [state, setState] as const
}

export default usePersistedState